/**
 * Soft NPS prompt rules (local prefs + engagement).
 *
 * When it may show (all must pass):
 * - authenticated
 * - at least 3 distinct check-in days
 * - has started at least one experiment (core loop used)
 * - not prompted this session
 * - not submitted in last 90 days
 * - not prompted in last 30 days
 * - not dismissed in last 14 days
 *
 * Caller should only open after a meaningful action (e.g. successful check-in),
 * never on cold login / first paint of Today.
 */

import {
  getPreference,
  setPreference,
} from "@/lib/capacitor/preferences";

const KEY = "andrum.nps_prefs";

export type NpsPrefs = {
  lastPromptAt: string | null;
  lastSubmitAt: string | null;
  lastDismissAt: string | null;
};

const DEFAULTS: NpsPrefs = {
  lastPromptAt: null,
  lastSubmitAt: null,
  lastDismissAt: null,
};

export async function loadNpsPrefs(): Promise<NpsPrefs> {
  const raw = await getPreference(KEY);
  if (!raw) return { ...DEFAULTS };
  try {
    return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<NpsPrefs>) };
  } catch {
    return { ...DEFAULTS };
  }
}

export async function saveNpsPrefs(prefs: NpsPrefs): Promise<void> {
  await setPreference(KEY, JSON.stringify(prefs));
}

function daysBetween(iso: string | null, now: Date): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const a = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const b = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((b - a) / 86_400_000);
}

/** Session flag: only one prompt attempt per page load */
let promptedThisSession = false;

export function resetNpsSessionFlagForTests(): void {
  promptedThisSession = false;
}

/**
 * Eligibility only — does not mean "show on login".
 * Open the sheet only after a completed core action (check-in / reflect).
 */
export async function shouldShowNps(input: {
  authenticated: boolean;
  checkInDays: number;
  /** User has started or completed at least one experiment */
  hasUsedExperiment: boolean;
  now?: Date;
}): Promise<boolean> {
  if (!input.authenticated) return false;
  if (input.checkInDays < 3) return false;
  if (!input.hasUsedExperiment) return false;
  if (promptedThisSession) return false;

  const now = input.now ?? new Date();
  const prefs = await loadNpsPrefs();

  const sinceSubmit = daysBetween(prefs.lastSubmitAt, now);
  if (sinceSubmit != null && sinceSubmit < 90) return false;

  const sincePrompt = daysBetween(prefs.lastPromptAt, now);
  if (sincePrompt != null && sincePrompt < 30) return false;

  const sinceDismiss = daysBetween(prefs.lastDismissAt, now);
  if (sinceDismiss != null && sinceDismiss < 14) return false;

  return true;
}

export async function markNpsPrompted(): Promise<void> {
  promptedThisSession = true;
  const prefs = await loadNpsPrefs();
  await saveNpsPrefs({
    ...prefs,
    lastPromptAt: new Date().toISOString(),
  });
}

export async function markNpsSubmitted(): Promise<void> {
  promptedThisSession = true;
  const prefs = await loadNpsPrefs();
  const iso = new Date().toISOString();
  await saveNpsPrefs({
    ...prefs,
    lastPromptAt: iso,
    lastSubmitAt: iso,
  });
}

export async function markNpsDismissed(): Promise<void> {
  promptedThisSession = true;
  const prefs = await loadNpsPrefs();
  const iso = new Date().toISOString();
  await saveNpsPrefs({
    ...prefs,
    lastPromptAt: iso,
    lastDismissAt: iso,
  });
}
